import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { dashboard } from '../services/api';

export default function BSRTracker({ accountId, inventory }) {
  const [selectedAsin, setSelectedAsin] = useState('');
  const [bsrData, setBsrData] = useState(null);
  const [manualBsr, setManualBsr] = useState({ bsr_rank: '', bsr_category: '' });
  const [days, setDays] = useState(30);

  const card = { background: '#fff', border: '0.5px solid rgba(0,0,0,0.08)', borderRadius: '12px', padding: '1.25rem', marginBottom: '12px' };
  const inp = { width: '100%', padding: '8px 10px', border: '0.5px solid #ddd', borderRadius: '7px', fontSize: '13px', boxSizing: 'border-box' };

  const loadBsr = useCallback(async (asin, d) => {
    if (!asin || !accountId) return;
    try {
      const res = await dashboard.bsr(accountId, asin, d);
      setBsrData(res);
    } catch (e) {
      console.error(e);
    }
  }, [accountId]);

  useEffect(() => {
    if (selectedAsin) loadBsr(selectedAsin, days);
  }, [selectedAsin, days, loadBsr]);

  const handleAddBsr = async () => {
    if (!selectedAsin || !manualBsr.bsr_rank) return;
    const item = inventory.find(i => i.asin === selectedAsin);
    await dashboard.addBsr(accountId, {
      asin: selectedAsin,
      sku: item?.sku,
      product_name: item?.product_name,
      bsr_rank: parseInt(manualBsr.bsr_rank),
      bsr_category: manualBsr.bsr_category,
    });
    setManualBsr({ bsr_rank: '', bsr_category: '' });
    loadBsr(selectedAsin, days);
  };

  const chartData = bsrData?.history?.map(r => ({
    date: new Date(r.recorded_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    bsr: r.bsr_rank,
  })) || [];

  return (
    <div>
      <div style={card}>
        <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '1rem' }}>BSR Tracker</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '1rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Select product</label>
            <select style={inp} value={selectedAsin} onChange={e => setSelectedAsin(e.target.value)}>
              <option value="">Choose a product...</option>
              {inventory.filter(i => i.asin).map(i => (
                <option key={i.asin} value={i.asin}>{i.sku} — {i.product_name || i.asin}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Date range</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {[7, 30, 90].map(d => (
                <button key={d} onClick={() => setDays(d)}
                  style={{ flex: 1, padding: '8px', borderRadius: '7px', border: '0.5px solid #ddd', background: days === d ? '#1a1a1a' : '#fff', color: days === d ? '#fff' : '#1a1a1a', fontSize: '12px', cursor: 'pointer' }}>
                  {d}d
                </button>
              ))}
            </div>
          </div>
        </div>

        {selectedAsin && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '1rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Add BSR manually</label>
              <input style={inp} type="number" placeholder="e.g. 1250" value={manualBsr.bsr_rank}
                onChange={e => setManualBsr(s => ({ ...s, bsr_rank: e.target.value }))} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: '500', color: '#666', marginBottom: '4px' }}>Category</label>
              <input style={inp} type="text" placeholder="e.g. Kitchen & Home" value={manualBsr.bsr_category}
                onChange={e => setManualBsr(s => ({ ...s, bsr_category: e.target.value }))} />
            </div>
            <button onClick={handleAddBsr}
              style={{ gridColumn: 'span 2', padding: '9px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>
              Add BSR entry
            </button>
          </div>
        )}
      </div>

      {bsrData && selectedAsin && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '10px', marginBottom: '12px' }}>
            {[
              { label: 'Avg daily BSR', value: bsrData.avg_daily ? '#' + bsrData.avg_daily.toLocaleString() : 'No data' },
              { label: 'Avg weekly BSR', value: bsrData.avg_weekly ? '#' + bsrData.avg_weekly.toLocaleString() : 'No data' },
              { label: 'Avg monthly BSR', value: bsrData.avg_monthly ? '#' + bsrData.avg_monthly.toLocaleString() : 'No data' },
            ].map(m => (
              <div key={m.label} style={{ background: '#f0efe9', borderRadius: '10px', padding: '14px 16px' }}>
                <div style={{ fontSize: '11px', color: '#777', marginBottom: '5px', fontWeight: '500' }}>{m.label}</div>
                <div style={{ fontSize: '20px', fontWeight: '500' }}>{m.value}</div>
              </div>
            ))}
          </div>

          {chartData.length > 0 ? (
            <div style={card}>
              <div style={{ fontSize: '12px', fontWeight: '500', color: '#777', marginBottom: '12px' }}>BSR history (lower is better)</div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={v => '#' + v.toLocaleString()} reversed={true} />
                  <Tooltip formatter={v => '#' + v.toLocaleString()} />
                  <Line type="monotone" dataKey="bsr" stroke="#378ADD" strokeWidth={2} dot={true} name="BSR" />
                </LineChart>
              </ResponsiveContainer>
              <p style={{ fontSize: '11px', color: '#999', marginTop: '8px' }}>Lower BSR = better performance</p>
            </div>
          ) : (
            <div style={{ ...card, textAlign: 'center', color: '#888', padding: '2rem' }}>
              No BSR data yet — add your first entry above!
            </div>
          )}
        </>
      )}
    </div>
  );
}